import { Input } from "@/components/ui/input";
import { ViewBookingAssignedAreasInnerFromJSON } from "collins-js-sdk";
import { ViewBooking } from "collins-js-sdk/models/ViewBooking";
import dayjs from "dayjs";
import { ChevronDown, LayoutTemplate, Minus, Plus, Search } from "lucide-react";
import {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useDeviceContext } from "../../../../context/DeviceContext";
import { useDiaryContext } from "../../../../hooks/useDiaryContext";
import { useNotificationsContext } from "../../../../hooks/useNotificationsContext";
import { BookableArea } from "../../../../types/venues";
import {
  getCustomerName,
  updateFloorPlanBooking
} from "../../../../utils/booking";
import { TOUCH_DRAG_MODE_EVENT } from "../../../../utils/dragCloneUtils";
import Loading from "../../../../utils/Loading";
import { WebViewPopover } from "../SubHeader/WebViewPopover";
import { FloorplanLegend } from "./components/FloorplanLegend";
import TableView from "./components/TableView";
import { RenderableTable } from "./floorplanTypes";
import {
  buildFloorplanConfig,
  getBookingsForFloorplan,
  getRotatedBoundingBox,
  transformLayout
} from "./floorplanUtils";
import { usePanZoom } from "./usePanZoom";

type BoundingBox = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  offsetX: number;
  offsetY: number;
  boundingWidth: number;
  boundingHeight: number;
};

const doesLabelOverlap = (
  table: RenderableTable,
  allTables: RenderableTable[],
  boundingBoxes: Map<string, BoundingBox>
): boolean => {
  if (table.bookings.length > 0) return false;

  const bb = boundingBoxes.get(table.configId);
  if (!bb) return false;

  const labelCentreX = table.x - bb.offsetX + bb.boundingWidth / 2;
  const labelTop = table.y - bb.offsetY - 20;
  const labelHeight = 16;
  const labelHalfWidth = table.width / 2;

  const labelLeft = labelCentreX - labelHalfWidth;
  const labelRight = labelCentreX + labelHalfWidth;
  const labelBottom = labelTop + labelHeight;

  for (const other of allTables) {
    if (other.configId === table.configId) continue;
    const otherBB = boundingBoxes.get(other.configId);
    if (!otherBB) continue;

    if (
      labelRight > otherBB.left &&
      labelLeft < otherBB.right &&
      labelBottom > otherBB.top &&
      labelTop < otherBB.bottom
    ) {
      return true;
    }
  }

  return false;
};

const FloorPlan = () => {
  const bookingContext = useDiaryContext();
  const {
    floorPlanConfig,
    venueConfig,
    setDiaryContext,
    bookingsFiltered,
    selectedTable,
    lastUpdatedTable,
    date,
    selectedFloorplan,
    isInitialising
  } = bookingContext;
  const { colourScheme } = useDeviceContext();
  const notificationsContext = useNotificationsContext();

  const [loading, setLoading] = useState<boolean | undefined>(undefined);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isFloorplanPickerOpen, setIsFloorplanPickerOpen] =
    useState<boolean>(false);
  const [searchCriteria, setSearchCriteria] = useState<string>("");
  const [hoveredTableId, setHoveredTableId] = useState<string | null>(null);

  const isDark = colourScheme === "dark";
  const theme = isDark ? ("dark" as const) : ("light" as const);

  const config = useMemo(
    () => buildFloorplanConfig(floorPlanConfig),
    [floorPlanConfig]
  );

  const floorplanBookings = useMemo(() => {
    if (!bookingsFiltered) return [];
    return getBookingsForFloorplan(bookingsFiltered, venueConfig);
  }, [bookingsFiltered, venueConfig]);

  const tables: RenderableTable[] = useMemo(() => {
    if (!selectedFloorplan || !venueConfig) return [];

    return transformLayout({
      items: selectedFloorplan.config ?? [],
      areas: venueConfig.bookable_areas ?? [],
      bookings: floorplanBookings,
      closures: venueConfig.bookable_areas_closed ?? [],
      showNonTable: floorPlanConfig.displayNonTableItems,
      theme,
      currentDate: dayjs(date),
      venueConfig
    });
  }, [
    selectedFloorplan,
    venueConfig,
    floorplanBookings,
    floorPlanConfig.displayNonTableItems,
    theme,
    date
  ]);

  const shouldShowTableNameInBadgeByConfigId = useMemo(() => {
    const results = new Map<string, boolean>();

    if (!floorPlanConfig.displayTableNames) {
      for (const t of tables) {
        results.set(t.configId, false);
      }
      return results;
    }

    const boundingBoxes = new Map<string, BoundingBox>();

    for (const t of tables) {
      const bb = getRotatedBoundingBox(t.width, t.height, t.rotation);
      const left = t.x - bb.offsetX;
      const top = t.y - bb.offsetY;
      boundingBoxes.set(t.configId, {
        left,
        top,
        right: left + bb.boundingWidth,
        bottom: top + bb.boundingHeight,
        offsetX: bb.offsetX,
        offsetY: bb.offsetY,
        boundingWidth: bb.boundingWidth,
        boundingHeight: bb.boundingHeight
      });
    }

    for (const t of tables) {
      results.set(t.configId, doesLabelOverlap(t, tables, boundingBoxes));
    }

    return results;
  }, [floorPlanConfig.displayTableNames, tables]);

  const tableBounds = useMemo(() => {
    if (tables.length === 0) return undefined;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const t of tables) {
      const bb = getRotatedBoundingBox(t.width, t.height, t.rotation);
      const left = t.x - bb.offsetX;
      const top = t.y - bb.offsetY;
      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, left + bb.boundingWidth);
      maxY = Math.max(maxY, top + bb.boundingHeight);
    }

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    if (contentWidth <= 0 || contentHeight <= 0) return undefined;

    return {
      minX,
      minY,
      maxX,
      maxY,
      contentWidth,
      contentHeight
    };
  }, [tables]);

  const handleContainerClick = useCallback(() => {
    setDiaryContext({ selectedTable: undefined });
  }, [setDiaryContext]);

  const {
    containerRef,
    zoomLevel,
    panOffset,
    isPanning,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom
  } = usePanZoom({
    tableBounds,
    selectedFloorplan,
    onContainerClick: handleContainerClick,
    loading: !!loading,
    tablesCount: tables.length,
    initialZoomLevel: floorPlanConfig.zoomLevel || 1
  });

  useEffect(() => {
    if (
      venueConfig &&
      (!venueConfig.floorplans || venueConfig.floorplans.length === 0)
    ) {
      if (selectedFloorplan) {
        setDiaryContext({ selectedFloorplan: undefined });
      }
      return;
    }
    if (!selectedFloorplan && venueConfig?.floorplans?.[0]) {
      setDiaryContext({ selectedFloorplan: venueConfig.floorplans[0] });
    }
  }, [venueConfig, selectedFloorplan, setDiaryContext]);

  useEffect(() => {
    if (isInitialising) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [selectedFloorplan, isInitialising]);

  useEffect(() => {
    const handleTouchDragMode = (event: Event) => {
      const customEvent = event as CustomEvent<{ active: boolean }>;
      setIsDragging(Boolean(customEvent.detail?.active));
    };

    window.addEventListener(
      TOUCH_DRAG_MODE_EVENT,
      handleTouchDragMode as EventListener
    );

    return () => {
      window.removeEventListener(
        TOUCH_DRAG_MODE_EVENT,
        handleTouchDragMode as EventListener
      );
    };
  }, []);

  const saveAreaChange = useCallback(
    async (areaId: string, areaName: string, booking: ViewBooking) => {
      const area: BookableArea = { id: areaId, name: areaName };

      setDiaryContext({ lastUpdatedTable: area });

      const updatedBooking: ViewBooking = {
        ...booking,
        assignedAreas: [
          ViewBookingAssignedAreasInnerFromJSON({ id: areaId, name: areaName })
        ]
      };

      await updateFloorPlanBooking(updatedBooking, bookingContext);

      notificationsContext.addNotification({
        heading: `${getCustomerName(booking)} (${booking.time}) is now allocated to ${areaName}.`,
        autoClose: 5000,
        toastType: "information"
      });
    },
    [bookingContext, setDiaryContext, notificationsContext]
  );

  const handleDropBooking = useCallback(
    (areaId: string, areaName: string, bookingData: string) => {
      try {
        const parsed: unknown = JSON.parse(bookingData);
        // Validate that we have the minimum required fields for a booking
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          typeof (parsed as Record<string, unknown>).id !== "string" ||
          typeof (parsed as Record<string, unknown>).time !== "string"
        ) {
          console.error("Invalid booking data shape:", parsed);
          notificationsContext.addNotification({
            heading: "Failed to move booking",
            autoClose: 5000,
            toastType: "error"
          });
          return;
        }
        const booking = parsed as ViewBooking;

        // If the booking spans multiple areas, warn the user before removing additional areas
        if (
          Array.isArray(booking.assignedAreas) &&
          booking.assignedAreas.length > 1
        ) {
          const confirmed = window.confirm(
            "This booking is over multiple areas. Moving it this way will remove it from all additional areas. Are you sure you want to do this?"
          );
          if (!confirmed) {
            return;
          }
        }

        saveAreaChange(areaId, areaName, booking);
      } catch (error) {
        console.error("Failed to parse booking data:", error);
        notificationsContext.addNotification({
          heading: "Failed to move booking",
          autoClose: 5000,
          toastType: "error"
        });
      }
    },
    [saveAreaChange, notificationsContext]
  );

  const handleSelectTable = useCallback(
    (configId: string | null) => {
      if (!configId) {
        setDiaryContext({ selectedTable: undefined });
        return;
      }
      const table = tables.find((t) => t.configId === configId);
      if (table?.area) {
        setDiaryContext({
          selectedTable: { id: table.area.id, name: table.name }
        });
      }
    },
    [tables, setDiaryContext]
  );

  const handleRequestAddBooking = useCallback(
    (area: RenderableTable["area"]) => {
      if (!area) return;
      setDiaryContext({
        selectedTable: undefined,
        gridAreaClick: {
          type: "double",
          newValue: { area, cell: -1 }
        }
      });
    },
    [setDiaryContext]
  );

  const floorplanData = useMemo(
    () =>
      venueConfig?.floorplans?.map((plan, i) => ({
        label: plan.name,
        value: i
      })) ?? [],
    [venueConfig?.floorplans]
  );

  const filteredFloorplans = useMemo(() => {
    if (!searchCriteria) return floorplanData;
    return floorplanData.filter((plan) =>
      plan.label.toLowerCase().includes(searchCriteria.toLowerCase())
    );
  }, [floorplanData, searchCriteria]);

  const selectedFloorplanLabel = useMemo(
    () => selectedFloorplan?.name ?? "Select floor plan",
    [selectedFloorplan]
  );

  const handleValueChanged = useCallback(
    (index: number) => {
      setIsFloorplanPickerOpen(false);
      setSearchCriteria("");
      setDiaryContext({ selectedFloorplan: venueConfig?.floorplans?.[index] });
    },
    [setDiaryContext, venueConfig?.floorplans]
  );

  const handleSearchCriteriaChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchCriteria(e.target.value);
    },
    []
  );

  const handleToggleFloorplanPicker = useCallback(() => {
    setIsFloorplanPickerOpen((prev) => !prev);
  }, []);

  const getOptionClassName = useCallback((isSelected: boolean): string => {
    const baseClasses =
      "w-full px-3 py-2 text-left text-sm focus:outline-none rounded-md";
    const selectedClasses = "bg-accent text-accent-foreground font-medium";
    const hoverClasses =
      "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground";
    return `${baseClasses} ${isSelected ? selectedClasses : hoverClasses}`;
  }, []);

  const hasNoFloorplan = tables.length === 0 && !selectedFloorplan;

  const floorplanContent = useMemo(() => {
    if (hasNoFloorplan && !loading) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-center p-8">
          <LayoutTemplate className="h-12 w-12 text-muted-foreground mb-6 opacity-50" />
          <h2 className="text-xl font-semibold mb-3">
            No Floor Plan Available
          </h2>
          <p className="text-muted-foreground text-sm max-w-md">
            A floor plan hasn&apos;t been set up for{" "}
            {venueConfig?.title ?? "this venue"} yet. Contact your organisation
            administrator to configure the floor layout and table arrangements.
          </p>
        </div>
      );
    }

    if (loading) {
      return <Loading />;
    }

    return (
      <div
        className={[
          "FloorplanViewerContainer",
          "flex flex-row flex-[1_100%] overflow-hidden opacity-100",
          "text-[var(--fieldLabel)]",
          "pb-[var(--safe-area-inset-bottom,0px)]"
        ].join(" ")}
        style={{
          overscrollBehavior: "none",
          height:
            "calc(100vh - 179px - var(--workspace-tab-adjustment, 0px) - var(--safe-area-inset-top, 0px))",
          transition: "opacity 2s ease-in-out"
        }}
      >
        <div
          ref={containerRef}
          className={[
            "FloorplanViewerInnerContainer",
            "h-full w-0 flex-grow",
            "border-none bg-transparent p-0 m-0 font-[inherit] text-[inherit] text-left outline-none",
            "flex flex-col",
            "focus-visible:outline-2 focus-visible:outline-[#6366f1] focus-visible:outline-offset-2",
            isDragging ? "is-drag-target" : ""
          ]
            .filter(Boolean)
            .join(" ")}
          role="application"
          aria-roledescription="interactive floor plan"
          aria-label="Floor plan view. Use arrow keys to pan, plus and minus to zoom."
          style={{
            cursor: isPanning ? "grabbing" : "grab",
            position: "relative",
            overflow: "hidden"
          }}
        >
          <div className="sr-only" aria-live="polite">
            {`Zoom ${Math.round(zoomLevel * 100)}%. Arrow keys: pan. +/−: zoom. Escape: deselect.`}
          </div>
          <div
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
              transformOrigin: "0 0",
              position: "relative",
              width: "100%",
              height: "100%"
            }}
          >
            {tables.map((table) => (
              <TableView
                key={table.configId}
                table={table}
                isSelected={
                  selectedTable?.id != null &&
                  table.area?.id != null &&
                  selectedTable.id === table.area.id
                }
                isAnySelected={!!selectedTable}
                isLastUpdated={lastUpdatedTable?.id === table.area?.id}
                shouldShowTableNameInBadge={
                  shouldShowTableNameInBadgeByConfigId.get(table.configId) ??
                  false
                }
                isDark={isDark}
                config={config}
                isDragging={isDragging}
                isPanning={isPanning}
                isHovered={hoveredTableId === table.configId}
                onSelectTable={handleSelectTable}
                onHoverTable={setHoveredTableId}
                onDropBooking={handleDropBooking}
                onRequestAddBooking={handleRequestAddBooking}
              />
            ))}
          </div>

          <div className="absolute bottom-4 left-4 z-50 flex items-center gap-2">
            <FloorplanLegend isDark={isDark} />
          </div>

          <div className="absolute bottom-4 right-4 z-50 flex items-center gap-1">
            <button
              onClick={handleZoomOut}
              className={`h-[44px] w-[44px] rounded-full border flex items-center justify-center transition-all hover:scale-105 backdrop-blur-xl shadow-lg ${
                isDark
                  ? "bg-card/80 border-border text-foreground"
                  : "bg-white/80 border-slate-200 text-slate-900"
              }`}
              aria-label="Zoom out"
            >
              <Minus size={16} />
            </button>
            <button
              onClick={handleResetZoom}
              className={`text-xs font-bold px-2 min-w-[44px] min-h-[44px] text-center cursor-pointer hover:underline ${
                isDark ? "text-white/60" : "text-slate-500"
              }`}
              aria-label="Reset zoom"
            >
              {Math.round(zoomLevel * 100)}%
            </button>
            <button
              onClick={handleZoomIn}
              className={`h-[44px] w-[44px] rounded-full border flex items-center justify-center transition-all hover:scale-105 backdrop-blur-xl shadow-lg ${
                isDark
                  ? "bg-card/80 border-border text-foreground"
                  : "bg-white/80 border-slate-200 text-slate-900"
              }`}
              aria-label="Zoom in"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }, [
    config,
    handleDropBooking,
    handleRequestAddBooking,
    handleResetZoom,
    handleSelectTable,
    hasNoFloorplan,
    hoveredTableId,
    isDark,
    isDragging,
    isPanning,
    lastUpdatedTable?.id,
    loading,
    panOffset.x,
    panOffset.y,
    selectedTable,
    shouldShowTableNameInBadgeByConfigId,
    tables,
    venueConfig?.title,
    zoomLevel
  ]);

  return (
    <div className="w-full h-full overflow-hidden">
      <div className="min-h-12 mx-2 my-0 rounded-md border-solid border-[--borderColor]">
        {(venueConfig?.floorplans?.length ?? 0) > 0 && (
          <WebViewPopover
            open={isFloorplanPickerOpen}
            onOpenChange={setIsFloorplanPickerOpen}
            side="bottom"
            sideOffset={8}
            content={
              <>
                <div className="p-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search Floor Plans"
                      value={searchCriteria}
                      onChange={handleSearchCriteriaChange}
                      className="pl-10"
                      autoFocus
                      aria-label="Search floor plans"
                    />
                  </div>
                </div>
                <div className="max-h-[300px] overflow-y-auto px-2 pb-2">
                  {filteredFloorplans.length > 0 ? (
                    filteredFloorplans.map((plan) => {
                      const isSelected = selectedFloorplan?.name === plan.label;
                      return (
                        <button
                          key={plan.value}
                          className={getOptionClassName(isSelected)}
                          onClick={() => handleValueChanged(plan.value)}
                          aria-pressed={isSelected}
                        >
                          {plan.label}
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      No floor plans found
                    </div>
                  )}
                </div>
              </>
            }
          >
            <button
              className="eds-button flex items-center justify-between w-full px-3 py-2 text-sm bg-background border-2 border-input rounded-md hover:bg-accent hover:text-accent-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleToggleFloorplanPicker}
              aria-expanded={isFloorplanPickerOpen}
              aria-haspopup="listbox"
            >
              <span className="truncate text-left font-medium">
                {selectedFloorplanLabel}
              </span>
              <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
            </button>
          </WebViewPopover>
        )}
      </div>
      {floorplanContent}
    </div>
  );
};

export default memo(FloorPlan);
